import axiosConfig from '../../../../../../redux/Spoolsbase/axiosConfig';
import type { CoatingSpoolPersistPayload } from './CoatingSpoolChecklistEditor';
import { COATING_CHECKLIST_SPEC_CATEGORY } from './coatingSpecificationConstants';

/** Same backend table as FAT (`/fat-specification-templates`); rows are keyed by `category`. */
const BASE = '/fat-specification-templates';

/** Coating-side row of `/fat-specification-templates` (mirrors `FatSpecificationTemplateDto`). */
export type CoatingSpecificationTemplateDto = {
  id: number;
  projectId: number;
  pipeId: number;
  /** 1-based spool slot; one template row per pipe + spool. */
  spoolIndex?: number;
  /** Always `COATING_CHECKLIST_SPEC_CATEGORY` for coating rows. */
  category: string;
  reference: string | null;
  payload: CoatingSpoolPersistPayload | null;
  createdAt: string | null;
  updatedAt: string | null;
};

function unwrapData<T>(res: { data?: unknown }): T {
  const root = res?.data as { data?: T } | undefined;
  if (root && typeof root === 'object' && 'data' in root && root.data !== undefined) {
    return root.data as T;
  }
  return root as T;
}

/** GET coating templates for the project / pipe; filters by `COATING_CHECKLIST_SPEC_CATEGORY` client-side. */
export async function listCoatingSpecificationTemplates(params: {
  projectId: string | number;
  pipeId?: string | number;
  spoolIndex?: string | number;
}): Promise<{
  projectId: number;
  pipeId: number | null;
  templates: CoatingSpecificationTemplateDto[];
}> {
  const { data } = await axiosConfig.get(BASE, {
    params: {
      project_id: params.projectId,
      ...(params.pipeId != null && params.pipeId !== '' ? { pipe_id: params.pipeId } : {}),
      ...(params.spoolIndex != null && params.spoolIndex !== '' ? { spool_index: params.spoolIndex } : {}),
    },
  });
  const root = unwrapData<{
    projectId: number;
    pipeId: number | null;
    templates: CoatingSpecificationTemplateDto[];
  }>(data);
  const all = Array.isArray(root?.templates) ? root.templates : [];
  return {
    projectId: root?.projectId ?? Number(params.projectId),
    pipeId: root?.pipeId ?? (params.pipeId != null ? Number(params.pipeId) : null),
    templates: all.filter((t) => t?.category === COATING_CHECKLIST_SPEC_CATEGORY),
  };
}

/** POST a coating template row (insert or update by `id`); same endpoint as FAT, distinguished by `category`. */
export async function saveCoatingSpecificationTemplate(body: {
  id?: number;
  projectId: number;
  pipeId: number;
  spoolIndex: number;
  reference?: string | null;
  payload: CoatingSpoolPersistPayload | null;
}): Promise<CoatingSpecificationTemplateDto> {
  const { data } = await axiosConfig.post(BASE, {
    projectId: body.projectId,
    pipeId: body.pipeId,
    spoolIndex: body.spoolIndex,
    ...(body.id != null ? { id: body.id } : {}),
    category: COATING_CHECKLIST_SPEC_CATEGORY,
    reference: body.reference ?? null,
    payload: body.payload,
  });
  return unwrapData(data);
}

/** Resolve the coating checklist row for a 1-based spool slot (mirrors FAT resolver). */
export function findCoatingSpecificationTemplateForSpool(
  list: CoatingSpecificationTemplateDto[],
  spoolIndex1Based: number,
  spoolLabel: string,
): CoatingSpecificationTemplateDto | null {
  const want = spoolIndex1Based;
  const label = spoolLabel.trim();
  for (const t of list) {
    if (t.category !== COATING_CHECKLIST_SPEC_CATEGORY) continue;
    const si = t.spoolIndex;
    if (si != null && si > 0 && si === want) return t;
  }
  for (const t of list) {
    if (t.category !== COATING_CHECKLIST_SPEC_CATEGORY) continue;
    if (t.spoolIndex != null && t.spoolIndex > 0) continue;
    if (want === 1 && (t.reference || '').trim() === label) return t;
  }
  if (want === 1) {
    const legacy = list.filter(
      (t) => t.category === COATING_CHECKLIST_SPEC_CATEGORY && (t.spoolIndex == null || t.spoolIndex < 1),
    );
    if (legacy.length === 1) return legacy[0] ?? null;
  }
  return null;
}
